import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Alert } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import CSearchBar from '../../components/CSearchBar';
import { type TScaffoldTableColumnConfig } from '../../components/TScaffoldTableColumnConfig';
import TimestampComponent from '../../components/TimestampComponent';
import XDPaginator from '../../components/layout/XDPaginator';
import { updateStateAttribute } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import { type IAugmentedBackendResponse } from '../../models/IAugmentedBackendResponse';
import { type IBrowsableTableInternalState } from '../../models/IBrowsableTableInternalState';
import { type IRequestState } from '../../models/IRequestState';
import { type ISortingMeta } from '../../models/ISortingMeta';
import { type TPageSize } from '../../models/TPageSize';
import CBrowsableTableColumnToggleModal from './CBrowsableTableColumnToggleModal';
import CTable from './CTable';
import { type ITableBaseProps } from './CTableBase';

interface TableRequestMeta {
  sorting: ISortingMeta;
  search: string;
  page: number;
  pageSize?: TPageSize;
}

type SortableTScaffoldTableColumnConfig = TScaffoldTableColumnConfig & { isSortable?: boolean };
type TEffectiveColumnToggle = boolean | null;

export interface IBrowsableTableProps extends ITableBaseProps {
  state?: IRequestState | undefined;
  tableMeta?: IAugmentedBackendResponse<any>['meta'];
  onUpdateTableMeta?: (meta: TableRequestMeta) => void;
  cacheEpochInSeconds?: number;
  columnConfig: SortableTScaffoldTableColumnConfig[];
  onUpdateInternalState: (internalState: IBrowsableTableInternalState) => void;
  searchBar?: boolean;
  enableColumnToggle?: boolean;
  defaultColumnToggleArray?: boolean[];
  onLocaleChange?: (meta: TableRequestMeta) => void;
  pageSize?: TPageSize;
}

interface State {
  searchText: string;
  effectiveColumnToggleArray?: TEffectiveColumnToggle[];
  modalColumnToggleArray?: TEffectiveColumnToggle[];
  showColumnToggleModal?: boolean;
  initializedLocale?: string;
}

export default function CBrowsableTable(props: IBrowsableTableProps) {
  const [state, setState] = useState<State>({ searchText: '' });
  const { localeDict } = useAppSelector((state) => state.dashboard);
  const previousLocaleDict = useRef(localeDict);

  const configureTableLabel = CLocalizedTextStringStatic('global.configureTableColumns');
  const resultsLabel = CLocalizedTextStringStatic('global.results');
  const allLabel = CLocalizedTextStringStatic('global.pageSizing.all');

  const columns = Object.keys(props.data ?? {});
  const initialColumnToggleArray = computeInitialColumnToggleArray(
    columns.length,
    props.columnConfig,
    props.defaultColumnToggleArray
  );
  const effectiveColumnToggleArray = state.effectiveColumnToggleArray ?? initialColumnToggleArray;
  const modalColumnToggleArray = state.modalColumnToggleArray ?? effectiveColumnToggleArray;
  const toggleableColumns = columns
    .map((column, ix) => ({ column, ix }))
    .filter(({ ix }) => effectiveColumnToggleArray[ix] !== null);

  const handleSort = (sortCol: number) => {
    props.onUpdateTableMeta?.({
      sorting: {
        sortCol,
        ascending:
          sortCol === props.tableMeta?.sorting?.[0].columnIndex
            ? props.tableMeta?.sorting?.[0].direction === 'asc'
              ? 'descending'
              : 'ascending'
            : 'ascending'
      },
      search: state.searchText,
      page: 1,
      pageSize: props.pageSize
    });
  };

  const handleOpenColumnToggleModal = () => {
    updateStateAttribute<State>(
      {
        showColumnToggleModal: true,
        modalColumnToggleArray: effectiveColumnToggleArray
      },
      setState
    );
  };

  const handleCloseColumnToggleModal = () => {
    updateStateAttribute<State>(
      {
        showColumnToggleModal: false,
        modalColumnToggleArray: effectiveColumnToggleArray
      },
      setState
    );
  };

  const handleApplyColumnToggleArray = () => {
    const nextColumnToggleArray = state.modalColumnToggleArray ?? effectiveColumnToggleArray;

    updateStateAttribute<State>(
      {
        effectiveColumnToggleArray: nextColumnToggleArray.some((e) => e === true)
          ? nextColumnToggleArray
          : effectiveColumnToggleArray,
        showColumnToggleModal: false
      },
      setState
    );
  };

  const handleColumnToggleChange = (columnIndex: number, checked: boolean) => {
    const nextColumnToggleArray = [...(state.modalColumnToggleArray ?? effectiveColumnToggleArray)];
    nextColumnToggleArray[columnIndex] = checked;

    updateStateAttribute<State>(
      {
        modalColumnToggleArray: nextColumnToggleArray.some((e) => e === true)
          ? nextColumnToggleArray
          : effectiveColumnToggleArray
      },
      setState
    );
  };

  useEffect(() => {
    const shouldResetColumnToggleArray =
      !state.effectiveColumnToggleArray ||
      state.effectiveColumnToggleArray.length !== initialColumnToggleArray.length ||
      state.effectiveColumnToggleArray.some((e, ix) => (e === null) !== (initialColumnToggleArray[ix] === null));

    if (shouldResetColumnToggleArray) {
      updateStateAttribute<State>(
        { effectiveColumnToggleArray: initialColumnToggleArray, modalColumnToggleArray: initialColumnToggleArray },
        setState
      );
    }
  }, [columns.length, props.columnConfig, props.defaultColumnToggleArray]);

  useEffect(() => {
    if (!localeDict) return;

    // Ignore the initial dictionary.
    if (!previousLocaleDict.current) {
      previousLocaleDict.current = localeDict;
      return;
    }

    if (previousLocaleDict.current === localeDict) return;

    previousLocaleDict.current = localeDict;

    props.onLocaleChange?.({
      sorting: {
        sortCol: props.tableMeta?.sorting?.[0].columnIndex ?? 0,
        ascending: props.tableMeta?.sorting?.[0].direction === 'asc' ? 'ascending' : 'descending'
      },
      page: props.tableMeta?.pagination.page ?? 1,
      search: state.searchText,
      pageSize: props.pageSize
    });
  }, [localeDict]);

  useEffect(() => {
    const dayJSObject = props.cacheEpochInSeconds ? dayjs(props.cacheEpochInSeconds) : dayjs();

    props.onUpdateInternalState({
      searchBar: (
        <CSearchBar
          initSearchText={state.searchText}
          disabled={props.state?.isLoading}
          onSubmit={(searchText) => {
            updateStateAttribute<State>({ searchText }, setState);

            props.onUpdateTableMeta?.({
              sorting: {
                sortCol: props.tableMeta?.sorting?.[0].columnIndex ?? 0,
                ascending: props.tableMeta?.sorting?.[0].direction === 'asc' ? 'ascending' : 'descending'
              },
              page: 1,
              search: searchText,
              pageSize: props.pageSize
            });
          }}
        />
      ),
      paginator: (
        <XDPaginator
          apiReqState={props.state}
          pagination={props.tableMeta?.pagination}
          retrievePageCallback={(page) => {
            props.onUpdateTableMeta?.({
              sorting: {
                sortCol: props.tableMeta?.sorting?.[0].columnIndex ?? 0,
                ascending: props.tableMeta?.sorting?.[0].direction === 'asc' ? 'ascending' : 'descending'
              },
              page,
              search: state.searchText,
              pageSize: props.pageSize
            });
          }}
          dontScrollOnReload
        />
      ),
      timestamp: (t) => (
        <>
          {!props.state?.isError && !!props.data && (t === 'short' || t === undefined) && (
            <span>
              <TimestampComponent timestamp={dayJSObject.toDate()} />
              {props.enableColumnToggle && (
                <>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <a onClick={handleOpenColumnToggleModal}>{configureTableLabel}</a>
                </>
              )}
              {props.pageSize !== undefined && (
                <>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  {resultsLabel}:&nbsp;
                  {([10, 50, -1] as TPageSize[]).map((size, ix) => (
                    <React.Fragment key={size}>
                      {ix > 0 && <> &bull; </>}
                      <a
                        style={{
                          textDecoration: size === (props.pageSize ?? 10) ? 'underline' : 'none'
                        }}
                        onClick={() => {
                          props.onUpdateTableMeta?.({
                            sorting: {
                              sortCol: props.tableMeta?.sorting?.[0].columnIndex ?? 0,
                              ascending: props.tableMeta?.sorting?.[0].direction === 'asc' ? 'ascending' : 'descending'
                            },
                            page: 1,
                            search: state.searchText,
                            pageSize: size
                          });
                        }}
                      >
                        {size < 0 ? allLabel : size}
                      </a>
                    </React.Fragment>
                  ))}
                </>
              )}
            </span>
          )}
        </>
      )
    });
  }, [
    props.tableMeta,
    props.cacheEpochInSeconds,
    props.data,
    props.state,
    props.enableColumnToggle,
    state.searchText,
    effectiveColumnToggleArray,
    props.pageSize
  ]);

  return (
    <>
      {Object.keys(props.data ?? {}).length > 0 && (
        <>
          <CTable
            {...props}
            columnConfig={[
              /* Basically, we are weaving in the sort buttons, regardless of whether the props.config exist */
              ...Array(Object.keys(props.data ?? {}).length)
                .fill({ undefined })
                .map((_, ix) => {
                  const cfg: SortableTScaffoldTableColumnConfig = props.columnConfig[ix] ?? {};
                  const effectiveColumnToggle = effectiveColumnToggleArray[ix];
                  return {
                    ...cfg,
                    span: effectiveColumnToggle === true ? cfg.span : 0,
                    header: {
                      render: (context) => {
                        return (
                          <div
                            onClick={() => {
                              if (cfg.isSortable !== false) {
                                handleSort(ix);
                              }
                            }}
                            style={{
                              cursor: cfg.isSortable !== false ? 'pointer' : 'default',
                              ...cfg.header?.style?.(context)
                            }}
                          >
                            {cfg.header?.render?.(context) ?? context.value}
                            {cfg.isSortable !== false && (props.tableMeta?.sorting?.length ?? 0) > 0 ? (
                              <CSortButton
                                isSortCol={props.tableMeta?.sorting?.[0].columnIndex === ix}
                                direction={props.tableMeta?.sorting?.[0].direction}
                              />
                            ) : (
                              <span className={'text-xl'} />
                            )}
                          </div>
                        );
                      }
                    }
                  } satisfies TScaffoldTableColumnConfig;
                })
            ]}
          />
        </>
      )}
      <CBrowsableTableColumnToggleModal
        id={props.id}
        open={!!state.showColumnToggleModal}
        toggleableColumns={toggleableColumns}
        modalColumnToggleArray={modalColumnToggleArray}
        onClose={handleCloseColumnToggleModal}
        onApply={handleApplyColumnToggleArray}
        onColumnToggleChange={handleColumnToggleChange}
      />
      {Object.keys(props.data ?? {}).length === 0 ||
        (Object.keys(Object.values(props.data ?? {})[0] ?? {}).length === 0 && (
          <CPageModule fullWidth style={{ paddingTop: 0, paddingBottom: 0 }}>
            <Alert severity={'info'}>
              <CLocalizedText dictKey={'errorEmptyList'} />
            </Alert>
          </CPageModule>
        ))}
    </>
  );
}

function computeInitialColumnToggleArray(
  columnCount: number,
  columnConfig: SortableTScaffoldTableColumnConfig[],
  defaultColumnToggleArray?: boolean[]
): TEffectiveColumnToggle[] {
  const useDefaultColumnToggleArray =
    defaultColumnToggleArray !== undefined && defaultColumnToggleArray.length >= columnCount;

  return Array(columnCount)
    .fill(undefined)
    .map((_, ix) => {
      const span = columnConfig[ix]?.span;

      if (span === 0) {
        return null;
      }

      return useDefaultColumnToggleArray ? defaultColumnToggleArray[ix] : span !== 0;
    });
}

function CSortButton(props: {
  isSortCol: boolean;
  direction: 'asc' | 'desc' | undefined;
  type?: 'string' | 'number' | 'arrows';
}) {
  return (
    <span className={'align-bottom relative'}>
      {!props.isSortCol && (
        <>
          <ArrowDropUp className={'absolute -top-1 left-0 opacity-30 !text-xl'} />
          <ArrowDropDown className={'absolute top-0.5 left-0 opacity-30 !text-xl'} />
        </>
      )}
      {props.isSortCol && props.direction === 'asc' && <ArrowDropUp className={'absolute -top-0.5 left-0 !text-xl'} />}
      {props.isSortCol && props.direction === 'desc' && (
        <ArrowDropDown className={'absolute -top-0.5 left-0 !text-xl'} />
      )}
    </span>
  );
}
